import { useEffect, useRef, useState } from "react";
import { Mic, MicOff } from "lucide-react";

/**
 * One-shot speech-to-text button using the Web Speech API. Tap mic, talk,
 * the recognized phrase is dropped into onResult(). Designed for caregivers
 * to add tiles at speed (talk → tile created).
 */
export default function VoiceInput({ lang = "en-US", onResult, "data-testid": testId }) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(true);
  const [error, setError] = useState("");
  const recRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSupported(!!SR);
    return () => {
      try { recRef.current?.stop?.(); } catch { /* ignore */ }
    };
  }, []);

  const start = () => {
    setError("");
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSupported(false); return; }
    const rec = new SR();
    rec.lang = lang;
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.continuous = false;
    rec.onresult = (e) => {
      const transcript = e.results?.[0]?.[0]?.transcript || "";
      if (transcript) onResult?.(transcript.trim());
    };
    rec.onerror = (e) => {
      setError(e?.error || "Couldn't hear you. Try again.");
      setListening(false);
    };
    rec.onend = () => setListening(false);
    recRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch (e) {
      setError(String(e?.message || e));
      setListening(false);
    }
  };

  const stop = () => {
    try { recRef.current?.stop?.(); } catch { /* ignore */ }
    setListening(false);
  };

  if (!supported) return null;

  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={listening ? stop : start}
        className={`pill-btn ${listening ? "danger" : ""}`}
        aria-pressed={listening}
        title="Use your voice to fill in"
        data-testid={testId || "voice-input-btn"}
      >
        {listening ? <MicOff size={16} /> : <Mic size={16} />}
        <span>{listening ? "Stop" : "Speak it"}</span>
      </button>
      {error && (
        <span className="text-xs text-[color:var(--coral-deep)]" data-testid="voice-input-error">
          {error}
        </span>
      )}
    </div>
  );
}
